import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { snakeCase } from 'lodash';
import { Text, Card, breakpointValues } from '@comicrelief/component-library';
import { getImage } from 'gatsby-plugin-image';
import { paddingOptionMapping } from '../../utils/optionMapping';
import ImagePropType from '../../commonPropTypes/commonPropTypes';

import renderRichText from '../RichText/RichText';
import {
  Wrapper,
  Copy,
  Grid,
  Items,
  Line
} from './Cards.style';

const Cards = ({ data }) => {
  const {
    backgroundColour: bgCards,
    layout,
    cards,
    smallBreakpointRowLayout,
    mediumBreakpointRowLayout,
    paddingAbove,
    paddingBelow
  } = data;

  const cardsBackground = snakeCase(bgCards || 'white');

  const changeColor = background => (background && (background.includes('dark') || background.includes('red') || background.includes('deep_violet') || background.includes('black')) ? 'white' : 'black');

  // Define media queries
  const isMobile = useMediaQuery({ query: `(max-width: ${breakpointValues.M - 1}px)` });
  const isTablet = useMediaQuery({ query: `(min-width: ${breakpointValues.M}px) and (max-width: ${breakpointValues.L - 1}px)` });

  // Map the human-readable labels to the actual values, falling back to defaults:
  const paddingTop = paddingAbove ? paddingOptionMapping[paddingAbove] : '2rem';
  const paddingBottom = paddingBelow ? paddingOptionMapping[paddingBelow] : '2rem';

  return (
    <Wrapper
      cardsBackground={cardsBackground}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >

      <Grid>
        {cards
          && cards.map((card, index) => {
            const {
              body: cardBody,
              id,
              image,
              backgroundColour,
              label,
              centredText,
              squaredCorners
            } = card;

            const thisImageData = getImage(image);
            const thisImageFallback = thisImageData?.images?.fallback?.src;
            const thisImageLow = thisImageData?.placeholder?.fallback;
            const thisImageSrcSet = thisImageData?.images?.sources[0]?.srcSet;

            const cardBgColour = snakeCase(backgroundColour || 'white');

            /**
              * Set background colour (that in turn sets text color) to
              * the paragraph Cards background when in row layout.
              * This is because a Card in row layout currently has a transparent background.
              * So the the paragraph Cards background needs to be referenced.
            */
            const [correctBgColor, setCorrectBgColor] = useState();

            useEffect(() => {
              const isRowLayoutMobile = (isMobile && smallBreakpointRowLayout);
              const isRowLayoutTablet = (isTablet && mediumBreakpointRowLayout);
              const calculatedBgColor = isRowLayoutMobile || isRowLayoutTablet
                ? cardsBackground : cardBgColour;

              setCorrectBgColor(calculatedBgColor);
            }, [
              isMobile,
              isTablet,
              smallBreakpointRowLayout,
              mediumBreakpointRowLayout,
              cardsBackground,
              cardBgColour
            ]);

            const description = image?.description;
            return (
              <Items key={id} layout={layout}>
                <Card
                  // Only pass image props if they exist
                  {...(thisImageFallback && { image: thisImageFallback })}
                  {...(thisImageLow && { imageLow: thisImageLow })}
                  {...(thisImageSrcSet && { images: thisImageSrcSet })}
                  backgroundColor={cardBgColour}
                  imageAltText={description}
                  squaredCorners={squaredCorners}
                  smallBreakpointRowLayout={smallBreakpointRowLayout}
                  mediumBreakpointRowLayout={mediumBreakpointRowLayout}
                >

                  {cardBody ? (
                    <Copy centred={centredText}>
                      {label ? (
                        <Text
                          tag="span"
                          color={changeColor(correctBgColor)}
                        >
                          {label}
                        </Text>
                      ) : null}
                      {renderRichText(cardBody, correctBgColor, 'card')}
                    </Copy>
                  ) : null}

                </Card>
                {/* Create dividing line between each card, except on last card */}
                {index !== cards.length - 1
                  && (
                    <Line
                      smallBreakpointRowLayout={smallBreakpointRowLayout}
                      mediumBreakpointRowLayout={mediumBreakpointRowLayout}
                    />
                  )}
              </Items>
            );
          })}
      </Grid>
    </Wrapper>
  );
};

Cards.propTypes = {
  data: PropTypes.shape({
    backgroundColour: PropTypes.string,
    body: PropTypes.shape({
      raw: PropTypes.string
    }),
    cards: PropTypes.arrayOf(PropTypes.shape({
      backgroundColour: PropTypes.string.isRequired,
      body: PropTypes.shape({
        raw: PropTypes.string
      }),
      centredText: PropTypes.bool,
      id: PropTypes.string.isRequired,
      image: ImagePropType,
      label: PropTypes.string,
      squaredCorners: PropTypes.bool,
      title: PropTypes.string.isRequired
    })),
    layout: PropTypes.string,
    mediumBreakpointRowLayout: PropTypes.bool,
    paddingOptions: PropTypes.string,
    smallBreakpointRowLayout: PropTypes.bool,
    squaredCorners: PropTypes.bool,
    paddingAbove: PropTypes.string,
    paddingBelow: PropTypes.string
  }).isRequired
};

export default Cards;
